import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  CheckCircle2,
  Clock3,
  FileClock,
  IndianRupee,
  RefreshCcw,
  Search,
  ShieldCheck,
  TrendingUp
} from 'lucide-react';
import { isAuthenticated, getUserRole } from '../../utils/auth';
import api from '../../utils/api';
import Sidebar from '../../components/Sidebar/sidebar';
import Header from '../../components/Header';
import Pagination from '../../components/Pagination/Pagination';
import './Renewals.css';
import { API_BASE_URL } from '../../utils/apiBase';

const STATUS_OPTIONS = ['All', 'Pending', 'In Progress', 'Renewed', 'Overdue'];
const BUCKET_OPTIONS = ['All', 'Expiring Soon', 'Overdue', 'Upcoming', 'Renewed'];

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(Number(value || 0));

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const addYears = (date, years) => {
  const next = new Date(date);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const makeDefaultConversionForm = (row = {}) => {
  const expiryDate = row.expiryDate ? new Date(row.expiryDate) : null;
  const validExpiry = expiryDate && !Number.isNaN(expiryDate.getTime());
  const startDate = validExpiry ? addDays(expiryDate, 1) : new Date();
  const endDate = addDays(addYears(startDate, 1), -1);

  return {
    policyNumber: '',
    policyIssueDate: formatDateForInput(new Date()),
    policyStartDate: formatDateForInput(startDate),
    policyEndDate: formatDateForInput(endDate),
    grossPremium: row.premiumAmount || '',
    gstPercentage: row.gstPercentage ?? '',
    gstAmount: row.gstAmount ?? '',
    netPremium: row.netPremium || '',
    brokeragePercentage: row.brokeragePercentage ?? '',
    totalBrokerage: row.totalBrokerage ?? '',
    policyToBeRenewed: true
  };
};

const getInitials = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'RN';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
};

const Renewals = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [renewals, setRenewals] = useState([]);
  const [permissions, setPermissions] = useState({
    canViewAll: false,
    canViewTeam: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [bucketFilter, setBucketFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: 'expiryDate',
    direction: 'asc'
  });
  const [activeRenewal, setActiveRenewal] = useState(null);
  const [renewalModalMode, setRenewalModalMode] = useState('details');
  const [renewalForm, setRenewalForm] = useState({
    status: 'Pending',
    nextFollowUpDate: '',
    notes: ''
  });
  const [conversionForm, setConversionForm] = useState(makeDefaultConversionForm());
  const [renewalFormError, setRenewalFormError] = useState('');
  const [conversionError, setConversionError] = useState('');
  const [converting, setConverting] = useState(false);

  const userRole = getUserRole();
  const isAdmin = userRole === 'admin';

  const loadRenewals = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get(`${API_BASE_URL}/api/renewals`);
      setRenewals(response.data?.renewals || []);
      setPermissions(
        response.data?.permissions || {
          canViewAll: false,
          canViewTeam: false
        }
      );
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load renewals.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRenewals();
  }, []);

  useEffect(() => {
    if (activeRenewal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [activeRenewal]);

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return { key, direction: 'asc' };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' Asc' : ' Desc';
  };

  const getSortableValue = (row, key) => {
    if (key === 'expiryDate') {
      const value = row.expiryDate ? new Date(row.expiryDate).getTime() : 0;
      return Number.isNaN(value) ? 0 : value;
    }

    if (key === 'premiumAmount') {
      return Number(row.premiumAmount || 0);
    }

    return String(row[key] || '').toLowerCase();
  };

  const filteredRenewals = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const rows = renewals.filter((row) => {
      const matchesSearch =
        !normalizedSearch ||
        [
          row.clientName,
          row.policyNumber,
          row.insurerName,
          row.contactPerson,
          row.phoneNumber
        ]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);

      const matchesStatus =
        statusFilter === 'All' || row.effectiveStatus === statusFilter;

      const matchesBucket =
        bucketFilter === 'All' || row.renewalBucket === bucketFilter;

      return matchesSearch && matchesStatus && matchesBucket;
    });

    return rows.sort((a, b) => {
      const valueA = getSortableValue(a, sortConfig.key);
      const valueB = getSortableValue(b, sortConfig.key);

      if (valueA < valueB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [renewals, searchTerm, statusFilter, bucketFilter, sortConfig]);

  const pageLast = currentPage * itemsPerPage;
  const pageFirst = pageLast - itemsPerPage;
  const currentRenewals = filteredRenewals.slice(pageFirst, pageLast);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, bucketFilter, itemsPerPage]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredRenewals.length / itemsPerPage));
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [filteredRenewals.length, itemsPerPage]);

  const renewalSummaryCards = useMemo(() => {
    const countByStatus = (status) =>
      renewals.filter((row) => row.effectiveStatus === status).length;
    const totalPremium = renewals.reduce((sum, row) => sum + Number(row.premiumAmount || 0), 0);

    return [
      {
        label: 'Total Renewals',
        value: renewals.length,
        trend: 'Policies in renewal view',
        icon: FileClock,
        tone: 'blue'
      },
      {
        label: 'Pending',
        value: countByStatus('Pending'),
        trend: 'Awaiting action',
        icon: Clock3,
        tone: 'amber'
      },
      {
        label: 'In Progress',
        value: countByStatus('In Progress'),
        trend: 'Currently being followed up',
        icon: TrendingUp,
        tone: 'violet'
      },
      {
        label: 'Renewed',
        value: countByStatus('Renewed'),
        trend: 'Successfully completed',
        icon: CheckCircle2,
        tone: 'green'
      },
      {
        label: 'Total Premium',
        value: formatCurrency(totalPremium),
        trend: 'Renewal premium exposure',
        icon: IndianRupee,
        tone: 'slate'
      }
    ];
  }, [renewals]);

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('All');
    setBucketFilter('All');
  };

  const openRenewalDetails = (row) => {
    setActiveRenewal(row);
    setRenewalModalMode('details');
    setRenewalFormError('');
    setConversionError('');
  };

  const openRenewalProcess = (row) => {
    setActiveRenewal(row);
    setRenewalModalMode('process');
    setRenewalForm({
      status: row.renewalTracking?.status || 'Pending',
      nextFollowUpDate: formatDateForInput(row.renewalTracking?.nextFollowUpDate),
      notes: row.renewalTracking?.notes || ''
    });
    setConversionForm(makeDefaultConversionForm(row));
    setRenewalFormError('');
    setConversionError('');
  };

  const loadConversionDefaults = async (row) => {
    if (!row?.policyName) return;

    try {
      const params = new URLSearchParams({ policyName: row.policyName });
      if (row.classOfPolicyId) {
        params.append('classOfPolicyId', row.classOfPolicyId);
      }

      const response = await api.get(`${API_BASE_URL}/api/add-policy/policy-details?${params.toString()}`);
      setConversionForm((current) => ({
        ...current,
        gstPercentage: response.data?.defaultGST ?? current.gstPercentage,
        brokeragePercentage: response.data?.defaultBrokeragePercentage ?? current.brokeragePercentage
      }));
    } catch (err) {
      setConversionError(err?.response?.data?.error || 'Unable to load GST and brokerage defaults.');
    }
  };

  useEffect(() => {
    if (!activeRenewal || renewalModalMode !== 'process') return;
    loadConversionDefaults(activeRenewal);
  }, [activeRenewal, renewalModalMode]);

  useEffect(() => {
    const gross = parseFloat(conversionForm.grossPremium);
    const gstRate = parseFloat(conversionForm.gstPercentage);
    const brokerageRate = parseFloat(conversionForm.brokeragePercentage);

    let nextNetPremium = '';
    let nextGstAmount = '';
    let nextTotalBrokerage = '';

    if (!Number.isNaN(gross) && !Number.isNaN(gstRate) && gstRate >= 0) {
      const netPremium = (gross * 100) / (100 + gstRate);
      const gstAmount = gross - netPremium;
      nextNetPremium = netPremium.toFixed(2);
      nextGstAmount = gstAmount.toFixed(2);

      if (!Number.isNaN(brokerageRate) && brokerageRate >= 0) {
        nextTotalBrokerage = ((netPremium * brokerageRate) / 100).toFixed(2);
      }
    }

    if (
      conversionForm.netPremium === nextNetPremium &&
      conversionForm.gstAmount === nextGstAmount &&
      conversionForm.totalBrokerage === nextTotalBrokerage
    ) {
      return;
    }

    setConversionForm((current) => ({
      ...current,
      netPremium: nextNetPremium,
      gstAmount: nextGstAmount,
      totalBrokerage: nextTotalBrokerage
    }));
  }, [
    conversionForm.grossPremium,
    conversionForm.gstPercentage,
    conversionForm.brokeragePercentage,
    conversionForm.netPremium,
    conversionForm.gstAmount,
    conversionForm.totalBrokerage
  ]);

  const closeRenewalModal = () => {
    setActiveRenewal(null);
    setRenewalModalMode('details');
    setRenewalForm({
      status: 'Pending',
      nextFollowUpDate: '',
      notes: ''
    });
    setConversionForm(makeDefaultConversionForm());
    setRenewalFormError('');
    setConversionError('');
  };

  const handleSaveRenewal = async () => {
    if (!activeRenewal) return;

    setSaving(true);
    setRenewalFormError('');

    try {
      await api.put(
        `${API_BASE_URL}/api/renewals/${activeRenewal._id}`,
        {
          status: renewalForm.status,
          notes: renewalForm.notes,
          nextFollowUpDate: renewalForm.nextFollowUpDate || null
        }
      );

      await loadRenewals();
      closeRenewalModal();
    } catch (err) {
      setRenewalFormError(err?.response?.data?.error || 'Failed to update renewal.');
    } finally {
      setSaving(false);
    }
  };

  const handleConversionChange = (event) => {
    const { name, value, type, checked } = event.target;
    setConversionForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
    setConversionError('');
  };

  const handleConvertRenewal = async () => {
    if (!activeRenewal) return;

    if (!conversionForm.policyNumber.trim()) {
      setConversionError('New policy number is required.');
      return;
    }

    if (!conversionForm.policyStartDate || !conversionForm.policyEndDate) {
      setConversionError('Policy start and end dates are required.');
      return;
    }

    setConverting(true);
    setConversionError('');

    try {
      await api.post(`${API_BASE_URL}/api/renewals/${activeRenewal._id}/convert`, {
        ...conversionForm,
        notes: renewalForm.notes || 'Renewal converted to new policy'
      });

      await loadRenewals();
      closeRenewalModal();
    } catch (err) {
      setConversionError(err?.response?.data?.error || 'Failed to convert renewal to policy.');
    } finally {
      setConverting(false);
    }
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  return (
    <div className="renewals-page">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      <div className={`renewals-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="renewals-sticky-header">
          <Header />
        </div>

        <div className="renewals-scroll-area">
          <section className="renewals-shell">
            <div className="renewals-heading">
              <div>
                <span className="renewals-eyebrow">Manage Policy</span>
                <h1>Renewals Management</h1>
                <p>
                  {isAdmin || permissions.canViewTeam
                    ? 'Track policies expiring in the next 31 days across your visible users.'
                    : 'Track your assigned policies expiring in the next 31 days.'}
                </p>
              </div>
              <div className="renewals-hero-stat">
                <span>Visible Records</span>
                <strong>{filteredRenewals.length}</strong>
                <small>{renewals.length} total renewals</small>
              </div>
            </div>

            <div className="renewals-summary-grid">
              {renewalSummaryCards.map(({ label, value, trend, icon: Icon, tone }) => (
                <article className={`renewals-summary-card tone-${tone}`} key={label}>
                  <div className="renewals-summary-icon">
                    <Icon size={20} />
                  </div>
                  <div>
                    <span>{label}</span>
                    <strong>{value}</strong>
                    <small>{trend}</small>
                  </div>
                </article>
              ))}
            </div>

            <div className="renewals-toolbar-card">
              <div className="renewals-toolbar-head">
                <div>
                  <h2>Renewal Register</h2>
                  <p>{filteredRenewals.length} records expiring in the next 31 days</p>
                </div>
                <div className="renewals-toolbar-actions">
                  <button type="button" className="renewals-ghost-btn" onClick={resetFilters}>
                    Reset
                  </button>
                  <button type="button" className="renewals-refresh-btn" onClick={loadRenewals} disabled={loading}>
                    <RefreshCcw size={16} />
                    {loading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>

              <div className="renewals-toolbar">
                <label className="renewals-search-field">
                  <Search size={18} />
                  <input
                    className="renewals-search"
                    type="text"
                    placeholder="Search client, policy, insurer, or contact"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </label>

                <label>
                  <span>Status</span>
                  <select
                    className="renewals-filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  <span>Bucket</span>
                  <select
                    className="renewals-filter"
                    value={bucketFilter}
                    onChange={(e) => setBucketFilter(e.target.value)}
                  >
                    {BUCKET_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {error && <div className="renewals-alert">{error}</div>}

            <div className="renewals-list">
              <div className="renewals-table-title">
                <div>
                  <h3>Renewals List</h3>
                  <p>Click a policy number to view details, or use Process to renew.</p>
                </div>
                <ShieldCheck size={22} />
              </div>

              <div className="renewals-table-wrap">
                <table className="renewals-table">
                  <thead>
                    <tr>
                      <th className="renewals-sticky-col renewals-sticky-client" onClick={() => handleSort('clientName')}>Client Name{getSortIndicator('clientName')}</th>
                      <th className="renewals-sticky-col renewals-sticky-policy" onClick={() => handleSort('policyNumber')}>Policy{getSortIndicator('policyNumber')}</th>
                      <th onClick={() => handleSort('expiryDate')}>Expiry Date{getSortIndicator('expiryDate')}</th>
                      <th onClick={() => handleSort('premiumAmount')}>Premium Amount{getSortIndicator('premiumAmount')}</th>
                      <th onClick={() => handleSort('insurerName')}>Insurer Name{getSortIndicator('insurerName')}</th>
                      <th onClick={() => handleSort('effectiveStatus')}>Status{getSortIndicator('effectiveStatus')}</th>
                      <th>Bucket</th>
                      {permissions.canViewAll || permissions.canViewTeam ? <th>Owner</th> : null}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <tr className="renewals-skeleton-row" key={`renewal-skeleton-${index}`}>
                          <td className="renewals-sticky-col renewals-sticky-client"><span /></td>
                          <td className="renewals-sticky-col renewals-sticky-policy"><span /></td>
                          <td><span /></td>
                          <td><span /></td>
                          <td><span /></td>
                          <td><span /></td>
                          <td><span /></td>
                          {permissions.canViewAll || permissions.canViewTeam ? <td><span /></td> : null}
                          <td><span /></td>
                        </tr>
                      ))
                    ) : filteredRenewals.length ? (
                      currentRenewals.map((row) => (
                        <tr key={row._id}>
                          <td className="renewals-sticky-col renewals-sticky-client" data-label="Client Name">
                            <div className="renewals-client-cell">
                              <span className="renewals-avatar">{getInitials(row.clientName)}</span>
                              <div>
                                <strong>{row.clientName || '-'}</strong>
                                <span>{row.clientType || '-'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="renewals-sticky-col renewals-sticky-policy" data-label="Policy">
                            <div className="renewals-policy-cell">
                              <button
                                type="button"
                                className="renewals-policy-link"
                                onClick={() => openRenewalDetails(row)}
                              >
                                {row.policyNumber || '-'}
                              </button>
                              <small>{row.policyName || row.classOfPolicyName || 'Policy details'}</small>
                            </div>
                          </td>
                          <td data-label="Expiry Date" className="renewals-date-cell">
                            <strong>{formatDate(row.expiryDate)}</strong>
                          </td>
                          <td data-label="Premium Amount" className="renewals-premium-cell">{formatCurrency(row.premiumAmount)}</td>
                          <td data-label="Insurer Name">
                            <strong>{row.insurerName || '-'}</strong>
                          </td>
                          <td data-label="Status">
                            <span className={`renewal-status-badge renewal-status-${String(row.effectiveStatus).toLowerCase().replace(/\s+/g, '-')}`}>
                              {row.effectiveStatus}
                            </span>
                          </td>
                          <td data-label="Bucket">
                            <span className="renewal-bucket-pill">{row.renewalBucket || '-'}</span>
                          </td>
                          {permissions.canViewAll || permissions.canViewTeam ? (
                            <td data-label="Owner">
                              <strong>{row.owner?.name || '-'}</strong>
                              <span>{row.owner?.employeeId || '-'}</span>
                            </td>
                          ) : null}
                          <td data-label="Actions">
                            <button
                              type="button"
                              className="renewals-process-btn"
                              onClick={() => openRenewalProcess(row)}
                            >
                              Process
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={permissions.canViewAll || permissions.canViewTeam ? 9 : 8}
                          className="renewals-empty"
                        >
                          <FileClock size={34} />
                          <strong>No renewals found</strong>
                          <span>No renewal policies match the current filters.</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {filteredRenewals.length > 0 && (
              <div className="renewals-pagination-row">
                <div className="renewals-pagination-meta">
                  <span>
                    Showing {pageFirst + 1}-{Math.min(pageLast, filteredRenewals.length)} of {filteredRenewals.length}
                  </span>
                  <small>Filtered renewal records</small>
                </div>

                <div className="renewals-pagination-controls">
                  <Pagination
                    totalItems={filteredRenewals.length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                </div>

                <div className="renewals-items-per-page">
                  <label>Items per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {activeRenewal && (
        <div className="renewal-modal-overlay" onClick={closeRenewalModal}>
          <div className="renewal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="renewal-modal-head">
              <div>
                <span className="renewal-modal-eyebrow">
                  {renewalModalMode === 'process' ? 'Renewal Workflow' : 'Policy Details'}
                </span>
                <h3>{renewalModalMode === 'process' ? 'Renewal Processing' : 'Renewal Policy Details'}</h3>
                <p>{activeRenewal.clientName} - {activeRenewal.policyNumber}</p>
              </div>
              <button type="button" className="renewal-modal-close" onClick={closeRenewalModal}>
                &times;
              </button>
            </div>

            <div className="renewal-modal-body">
              <div className="renewal-processing-strip">
                <div>
                  <span>Policy</span>
                  <strong>{activeRenewal.policyNumber || '-'}</strong>
                </div>
                <div>
                  <span>Expiry</span>
                  <strong>{formatDate(activeRenewal.expiryDate)}</strong>
                </div>
                <div>
                  <span>Premium</span>
                  <strong>{formatCurrency(activeRenewal.premiumAmount)}</strong>
                </div>
                <div>
                  <span>Status</span>
                  <strong>{activeRenewal.effectiveStatus || '-'}</strong>
                </div>
              </div>

              {renewalModalMode === 'process' && (
                <>
                  <div className="renewal-modal-grid">
                    <div className="renewal-modal-card">
                      <label>Status</label>
                      <select
                        value={renewalForm.status}
                        onChange={(e) => setRenewalForm((current) => ({ ...current, status: e.target.value }))}
                      >
                        {VALID_STATUSES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="renewal-modal-card">
                      <label>Next Follow-up Date</label>
                      <input
                        type="date"
                        value={renewalForm.nextFollowUpDate}
                        onChange={(e) =>
                          setRenewalForm((current) => ({
                            ...current,
                            nextFollowUpDate: e.target.value
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="renewal-modal-card renewal-modal-notes">
                    <label>Notes / Remarks</label>
                    <textarea
                      rows="4"
                      value={renewalForm.notes}
                      onChange={(e) => setRenewalForm((current) => ({ ...current, notes: e.target.value }))}
                      placeholder="Add follow-up notes, decisions, or renewal remarks"
                    />
                  </div>

                  <div className="renewal-modal-card renewal-convert-card">
                    <div className="renewal-card-head">
                      <div>
                        <h4>Convert to Policy</h4>
                        <p>Create the renewed policy record from this expiring policy.</p>
                      </div>
                      <span>New Policy</span>
                    </div>

                    <div className="renewal-convert-grid">
                      <label>
                        <span>New Policy Number *</span>
                        <input
                          name="policyNumber"
                          value={conversionForm.policyNumber}
                          onChange={handleConversionChange}
                          placeholder="Enter renewed policy number"
                        />
                      </label>

                      <label>
                        <span>Policy Issue Date</span>
                        <input
                          type="date"
                          name="policyIssueDate"
                          value={conversionForm.policyIssueDate}
                          onChange={handleConversionChange}
                        />
                      </label>

                      <label>
                        <span>Policy Start Date *</span>
                        <input
                          type="date"
                          name="policyStartDate"
                          value={conversionForm.policyStartDate}
                          onChange={handleConversionChange}
                        />
                      </label>

                      <label>
                        <span>Policy End Date *</span>
                        <input
                          type="date"
                          name="policyEndDate"
                          value={conversionForm.policyEndDate}
                          onChange={handleConversionChange}
                        />
                      </label>

                      <label>
                        <span>Gross Premium</span>
                        <input
                          type="number"
                          name="grossPremium"
                          value={conversionForm.grossPremium}
                          onChange={handleConversionChange}
                        />
                      </label>

                      <label>
                        <span>Net Premium</span>
                        <input
                          type="number"
                          name="netPremium"
                          value={conversionForm.netPremium}
                          readOnly
                        />
                      </label>

                      <label>
                        <span>GST Percentage</span>
                        <input
                          type="number"
                          name="gstPercentage"
                          value={conversionForm.gstPercentage}
                          readOnly
                        />
                      </label>

                      <label>
                        <span>GST Amount</span>
                        <input
                          type="number"
                          name="gstAmount"
                          value={conversionForm.gstAmount}
                          readOnly
                        />
                      </label>

                      {isAdmin && (
                        <>
                          <label>
                            <span>Brokerage Percentage</span>
                            <input
                            type="number"
                            name="brokeragePercentage"
                            value={conversionForm.brokeragePercentage}
                            readOnly
                          />
                        </label>

                          <label>
                            <span>Total Brokerage</span>
                            <input
                            type="number"
                            name="totalBrokerage"
                            value={conversionForm.totalBrokerage}
                            readOnly
                          />
                        </label>
                        </>
                      )}

                      <label className="renewal-convert-check">
                        <input
                          type="checkbox"
                          name="policyToBeRenewed"
                          checked={conversionForm.policyToBeRenewed}
                          onChange={handleConversionChange}
                        />
                        <span>Show this new policy in future renewals</span>
                      </label>
                    </div>

                    {conversionError && <div className="renewals-alert renewal-modal-error">{conversionError}</div>}

                    <div className="renewal-convert-actions">
                      <button type="button" className="renewal-btn-primary" onClick={handleConvertRenewal} disabled={converting}>
                        {converting ? 'Converting...' : 'Convert to Policy'}
                      </button>
                    </div>
                  </div>
                </>
              )}

              <div className="renewal-modal-card renewal-modal-summary">
                <h4>Policy Snapshot</h4>
                <div className="renewal-summary-grid">
                  <p><strong>Client:</strong> {activeRenewal.clientName || '-'}</p>
                  <p><strong>Client Type:</strong> {activeRenewal.clientType || '-'}</p>
                  <p><strong>Policy No:</strong> {activeRenewal.policyNumber || '-'}</p>
                  <p><strong>Policy Name:</strong> {activeRenewal.policyName || activeRenewal.classOfPolicyName || '-'}</p>
                  <p><strong>Insurer:</strong> {activeRenewal.insurerName || '-'}</p>
                  <p><strong>Branch:</strong> {activeRenewal.branchName || '-'}</p>
                  <p><strong>Expiry:</strong> {formatDate(activeRenewal.expiryDate)}</p>
                  <p><strong>Premium:</strong> {formatCurrency(activeRenewal.premiumAmount)}</p>
                  <p><strong>Status:</strong> {activeRenewal.effectiveStatus || '-'}</p>
                  <p><strong>Bucket:</strong> {activeRenewal.renewalBucket || '-'}</p>
                  <p><strong>Contact Person:</strong> {activeRenewal.contactPerson || '-'}</p>
                  <p><strong>Contact:</strong> {activeRenewal.phoneNumber || '-'}</p>
                  <p><strong>Email:</strong> {activeRenewal.email || '-'}</p>
                  <p><strong>Location:</strong> {[activeRenewal.city, activeRenewal.state].filter(Boolean).join(', ') || '-'}</p>
                  {(permissions.canViewAll || permissions.canViewTeam) ? (
                    <p><strong>Owner:</strong> {activeRenewal.owner?.name || '-'}</p>
                  ) : null}
                  <p><strong>Next Follow-up:</strong> {formatDate(activeRenewal.renewalTracking?.nextFollowUpDate)}</p>
                </div>
              </div>

              <div className="renewal-modal-card renewal-modal-log">
                <h4>Activity Log</h4>
                {activeRenewal.renewalLogs?.length ? (
                  <div className="renewal-log-list">
                    {activeRenewal.renewalLogs.map((log, index) => (
                      <div key={`${log.updatedAt || index}-${index}`} className="renewal-log-item">
                        <strong>{log.status}</strong>
                        <span>{formatDate(log.updatedAt)}</span>
                        <span>{log.updatedBy?.userName || '-'}</span>
                        <p>{log.notes || '-'}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="renewal-log-empty">No renewal updates recorded yet.</p>
                )}
              </div>

              {renewalFormError && <div className="renewals-alert renewal-modal-error">{renewalFormError}</div>}
            </div>

            <div className="renewal-modal-actions">
              <button type="button" className="renewal-btn-secondary" onClick={closeRenewalModal}>
                {renewalModalMode === 'process' ? 'Cancel' : 'Close'}
              </button>
              {renewalModalMode === 'process' && (
                <button type="button" className="renewal-btn-primary" onClick={handleSaveRenewal} disabled={saving}>
                  {saving ? 'Saving...' : 'Update Renewal'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const VALID_STATUSES = ['Pending', 'In Progress', 'Renewed'];

export default Renewals;
